import React from "react";
import { toast } from "sonner";
import { Check } from "@phosphor-icons/react";
import { MAX_ON_COURT, blankAdd } from "@/hooks/useScorekeeper";
import { Stepper, ShotBtn, StatBtn } from "./StatButtons";

const allowDrop = (e) => e.preventDefault();

const PlayerTile = ({ sk, side, pid, zone, player, setPlayer }) => {
  const { playerById, adds, dragRef, handleDrop } = sk;
  const p = playerById(pid);
  if (!p) return null;
  const selected = player === pid;
  const ineligible = p.eligible === false;
  const pf = adds[pid]?.pf || 0;
  const tech = adds[pid]?.tech || 0;
  const tile = (
    <button
      type="button"
      draggable
      onDragStart={(e) => { dragRef.current = { side, pid }; e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", pid); }}
      onClick={() => { if (!selected && ineligible) toast.warning(`${p.name} is not eligible: ${(p.eligibility_reasons || []).join(", ")}`); setPlayer(selected ? "" : pid); }}
      data-testid={`${side}-player-tile-${pid}`}
      aria-pressed={selected}
      className={`w-full relative flex flex-col items-center justify-center px-2 py-4 border cursor-grab active:cursor-grabbing transition-all duration-200 ${
        selected ? "bg-[#FF5722] border-[#FF5722] text-black shadow-[0_0_14px_rgba(255,87,34,0.55)]" : ineligible ? "bg-[#1A1A1A] border-[#FF3B30]/50 text-white/80 hover:border-[#FF3B30]" : "bg-[#1A1A1A] border-white/15 text-white/80 hover:border-[#FF5722]"
      }`}
    >
      {selected && (
        <span className="absolute top-1.5 right-1.5 flex items-center justify-center w-5 h-5 rounded-full bg-black text-[#FF5722]"><Check size={13} weight="bold" /></span>
      )}
      {ineligible && !selected && (
        <span data-testid={`${side}-ineligible-badge-${pid}`} title={(p.eligibility_reasons || []).join(", ")} className="absolute top-1 left-1 text-[8px] uppercase font-bold bg-[#FF3B30] text-white px-1 py-0.5 leading-none">Not Eligible</span>
      )}
      <span className={`font-display text-3xl leading-none tabular-nums ${selected ? "text-black" : "text-[#FF5722]"}`}>{p.number != null ? `#${p.number}` : "—"}</span>
      <span className={`text-sm font-semibold mt-1 text-center leading-tight line-clamp-2 uppercase ${selected ? "text-black" : ""}`}>{p.name}</span>
      {(pf > 0 || tech > 0) && (
        <span className={`mt-1 text-[10px] font-bold tabular-nums ${selected ? "text-black/70" : "text-white/50"}`}>
          {pf > 0 ? `${pf} PF${pf >= 5 ? " OUT" : ""}` : ""}{pf > 0 && tech > 0 ? " · " : ""}{tech > 0 ? `${tech} T${tech >= 2 ? " EJ" : ""}` : ""}
        </span>
      )}
    </button>
  );
  if (zone === "court") {
    return <div onDragOver={allowDrop} onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleDrop(side, { type: "court-player", pid }); }}>{tile}</div>;
  }
  return tile;
};

export const TeamPanel = ({ sk, side, teamId, score, player, setPlayer }) => {
  const {
    teamOf, rosterOf, playerById, adds, onCourt, flash, handleDrop, subtractPoint,
    recordShot, recordStat, recordFoul, recordTech, addPlayerDelay, teamFoulsOf,
    bump, setTimeouts, timeouts, playerDelays, decPlayerDelay,
  } = sk;
  const t = teamOf(teamId);
  const subbing = rosterOf(teamId).filter((p) => !onCourt[side].includes(p.id));
  const selP = player ? playerById(player) : null;
  const a = player ? (adds[player] || blankAdd()) : blankAdd();

  return (
    <div className="border border-white/10 bg-[#121212] p-4 sm:p-6 flex flex-col items-center" data-testid={`scorekeeper-${side}`}>
      <div className="flex items-center gap-2 mb-1">
        <span className="w-10 h-10 flex items-center justify-center text-black font-display text-lg overflow-hidden" style={{ background: t?.primary_color || "#FF5722" }}>
          {t?.logo_url ? <img src={t.logo_url} alt="" className="w-full h-full object-cover" /> : (t?.name?.charAt(0) || "?")}
        </span>
        <span className="font-display text-2xl sm:text-3xl uppercase truncate">{t?.name || "TBD"}</span>
      </div>
      <div className="relative my-2 flex items-center gap-3">
        <button data-testid={`${side}-subtract`} onClick={() => subtractPoint(side)} disabled={score <= 0} aria-label="Correct: subtract 1 point" className="w-9 h-9 shrink-0 border border-white/15 hover:border-[#FF3B30] hover:text-[#FF3B30] text-white/50 text-2xl leading-none disabled:opacity-30 transition-all duration-200">−</button>
        <div className="font-display text-7xl sm:text-8xl text-[#FF5722] tabular-nums leading-none" data-testid={`${side}-score`}>{score}</div>
        {flash.side === side && (
          <span key={flash.ts} data-testid={`${side}-flash`} className={`animate-flash-badge absolute -top-1 right-0 font-display text-2xl whitespace-nowrap pointer-events-none ${flash.positive ? "text-[#00E676]" : "text-[#FF3B30]"}`}>
            {flash.label}
          </span>
        )}
      </div>

      {/* Selected player readout */}
      <p className="text-xs text-center text-white/50 mb-3 min-h-[2rem]" data-testid={`${side}-stat-readout`}>
        {selP
          ? <>Tracking <span className="text-[#FF5722] font-semibold uppercase">{selP.number != null ? `#${selP.number} ` : ""}{selP.name}</span><br />{a.pts} PTS · {a.tpm} 3PM · {a.reb} REB · {a.ast} AST · {a.stl} STL · {a.blk} BLK · {a.pf} PF</>
          : <span className="text-white/30">Select a player, then log their stats below</span>}
      </p>

      {/* Scoring — made shots only */}
      <div className="grid grid-cols-3 gap-2 w-full mb-2">
        <ShotBtn made testid={`${side}-ft-make`} onClick={() => recordShot(side, "ft", true)}>FT +1</ShotBtn>
        <ShotBtn made testid={`${side}-2pt-make`} onClick={() => recordShot(side, "2pt", true)}>2PT +2</ShotBtn>
        <ShotBtn made testid={`${side}-3pt-make`} onClick={() => recordShot(side, "3pt", true)}>3PT +3</ShotBtn>
      </div>
      {/* Rebound / Assist / Steal / Block */}
      <div className="grid grid-cols-4 gap-2 w-full mb-2">
        <StatBtn testid={`${side}-reb`} onClick={() => recordStat(side, "reb")}>REB</StatBtn>
        <StatBtn testid={`${side}-ast`} onClick={() => recordStat(side, "ast")}>AST</StatBtn>
        <StatBtn testid={`${side}-stl`} onClick={() => recordStat(side, "stl")}>STL</StatBtn>
        <StatBtn testid={`${side}-blk`} onClick={() => recordStat(side, "blk")}>BLK</StatBtn>
      </div>
      {/* Fouls */}
      <div className="grid grid-cols-3 gap-2 w-full mb-4">
        <StatBtn testid={`${side}-foul`} accent="#FF3B30" onClick={() => recordFoul(side)}>Foul</StatBtn>
        <StatBtn testid={`${side}-tech`} accent="#FFEA00" onClick={() => recordTech(side)}>Technical</StatBtn>
        <StatBtn testid={`${side}-delay`} accent="#FF9800" onClick={() => addPlayerDelay(side)}>Delay</StatBtn>
      </div>

      <div className="w-full" data-testid={`${side}-roster-tiles`}>
        <p className="text-xs uppercase tracking-[0.15em] text-white/50 mb-2 text-center">
          On Court: <span className={onCourt[side].length === MAX_ON_COURT ? "text-[#00E676]" : "text-white/70"}>{onCourt[side].length}/{MAX_ON_COURT}</span> · drag to sub
        </p>
        {rosterOf(teamId).length === 0 ? (
          <p className="text-white/40 text-sm text-center py-2">No players on this roster.</p>
        ) : (
          <>
            <div onDragOver={allowDrop} onDrop={() => handleDrop(side, { type: "court" })} data-testid={`${side}-oncourt-zone`} className="border-2 border-[#FF5722] bg-[#FF5722]/5 p-2 sm:p-3 min-h-[90px]">
              {onCourt[side].length === 0 ? (
                <p className="text-white/30 text-xs text-center py-5">Drag up to {MAX_ON_COURT} players here</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                  {onCourt[side].map((pid) => <PlayerTile key={pid} sk={sk} side={side} pid={pid} zone="court" player={player} setPlayer={setPlayer} />)}
                </div>
              )}
            </div>
            <div className="border-t-2 border-dashed border-white/20 mt-3 pt-2">
              <p className="text-xs uppercase tracking-[0.15em] text-white/40 mb-2 text-center">Subbing</p>
              <div onDragOver={allowDrop} onDrop={() => handleDrop(side, { type: "bench" })} data-testid={`${side}-subbing-zone`} className="min-h-[64px]">
                {subbing.length === 0 ? (
                  <p className="text-white/30 text-xs text-center py-5">Drag players here to bench them</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                    {subbing.map((p) => <PlayerTile key={p.id} sk={sk} side={side} pid={p.id} zone="bench" player={player} setPlayer={setPlayer} />)}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <div className="w-full mt-4 border-t border-white/10 pt-4 grid grid-cols-2 gap-3">
        <div className="text-center">
          <div className="text-xs uppercase tracking-[0.15em] text-white/50 mb-1">Team Fouls</div>
          <div className="flex items-center justify-center gap-2 h-10">
            <span data-testid={`${side}-fouls`} className="font-display text-3xl tabular-nums text-center">{teamFoulsOf(side)}</span>
          </div>
          <div className="text-[10px] uppercase tracking-wide text-white/30 mt-0.5">Auto (sum of player fouls)</div>
        </div>
        <div className="text-center">
          <div className="text-xs uppercase tracking-[0.15em] text-white/50 mb-1">Timeouts</div>
          <div className="flex items-center justify-center gap-2">
            <Stepper onClick={() => bump(setTimeouts, side, -1)} testid={`${side}-timeout-minus`}>−</Stepper>
            <span data-testid={`${side}-timeouts`} className="font-display text-3xl tabular-nums w-9 text-center">{timeouts[side]}</span>
            <Stepper onClick={() => bump(setTimeouts, side, 1)} testid={`${side}-timeout-plus`}>+</Stepper>
          </div>
        </div>
      </div>

      <div className="w-full flex flex-wrap gap-1.5 mt-3" data-testid={`${side}-player-delays`}>
        {rosterOf(teamId).filter((p) => playerDelays[p.id]).map((p) => (
          <button key={p.id} onClick={() => decPlayerDelay(p.id)} title="Remove a delay of game" className="text-xs uppercase bg-[#FF9800]/15 text-[#FF9800] px-2 py-1 flex items-center gap-1 hover:bg-[#FF9800]/25 transition-colors">
            DOG · {`${p.number != null ? `#${p.number} ` : ""}${p.name}`} · {playerDelays[p.id]}
          </button>
        ))}
      </div>
    </div>
  );
};
